import { portalPaths } from '@provision/portal-routes/dist/paths';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { useRegisterQuickActions } from 'src/components/QuickActions/use-register-quick-actions';
import {
  Address,
  PartnerGroupRules,
  PartnerSetting,
  Project,
  usePartnerGroupRules,
  usePartnerSettings,
} from 'src/graphql';
import { EntityNames, NullableDeepPartial } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { Modals, ModalsByEntity, useShowModals } from 'src/store/modals';
import { ClientRaw } from 'src/types/api-types';
import { useNavigateRoute } from 'src/utils/routing';
import { useThemeSwitch } from './use-theme-switch';

const THEME_QUICK_ACTION_ICONS = {
  light: 'sun',
  dark: 'moon',
  auto: 'monitor',
} as const;

export const useGlobalQuickActions = () => {
  const { navigate } = useNavigateRoute();
  const { setForceThemeMode, themeOptions } = useThemeSwitch();
  const [showConversationModal, showClientModal, showSiteModal, showProjectModal] =
    useShowModals(
      Modals.AddConversationModal,
      ModalsByEntity[EntityNames.Client],
      ModalsByEntity[EntityNames.Site],
      ModalsByEntity[EntityNames.Project],
    );

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const isMultiTenancyMode =
    useGlobalStore.use.isMultiTenancyMode() && isNullish(selectedPartnerId);

  const { partnerGroupRules } = usePartnerGroupRules(selectedPartnerId);

  const { data: disabledProvisionProjects } = usePartnerSettings({
    partnerSetting: PartnerSetting.DisabledProvisionProjects,
    partnerId: selectedPartnerId,
    splitPartnerAndPlatformData: true,
  });

  const hasProjectAccess =
    !isMultiTenancyMode &&
    partnerGroupRules[PartnerGroupRules.AccessToProvisionProjects] &&
    disabledProvisionProjects.partnerLevel?.[0]?.toLowerCase() !== 'true';

  const quickActions = useMemo(
    () => [
      {
        id: EntityNames.Conversation,
        title: 'New conversation',
        description: 'Create a new support conversation',
        icon: <DynamicIcon name="messages-square" library="lucide" />,
        handler: showConversationModal,
        order: 1,
      },
      {
        id: EntityNames.Client,
        title: 'New client',
        description: 'Create a new client account',
        icon: <DynamicIcon name="users" library="lucide" />,
        handler: () =>
          showClientModal({
            onSuccessSubmit: (client: NullableDeepPartial<ClientRaw>) => {
              if (client.clientId) {
                navigate(portalPaths.client.full(client.clientId));
              }
            },
          }),
        order: 2,
      },
      {
        id: EntityNames.Site,
        title: 'New site',
        description: 'Add a new customer site',
        icon: <DynamicIcon name="building-2" library="lucide" />,
        handler: () => {
          showSiteModal({
            onSuccessSubmit: (site: Address) => {
              if (site.addressId) {
                navigate(portalPaths.site.full(site.addressId));
              }
            },
          });
        },
        order: 3,
      },
      ...(hasProjectAccess
        ? [
            {
              id: EntityNames.Project,
              title: 'New project',
              description: 'Start a new project',
              icon: <DynamicIcon name="folder-open" library="lucide" />,
              handler: () => {
                showProjectModal({
                  onSuccessSubmit: (project: Project) => {
                    if (project.id) {
                      navigate(
                        portalPaths.entity.all({
                          entityName: 'projects',
                          id: project.id,
                        }),
                      );
                    }
                  },
                });
              },
              order: 4,
            },
          ]
        : []),
      ...themeOptions.map((option) => ({
        id: `theme-${option.value ?? 'auto'}`,
        title: `Use ${option.label.toLowerCase()} theme`,
        description: `Switch to the ${option.label.toLowerCase()} theme`,
        icon: (
          <DynamicIcon
            name={THEME_QUICK_ACTION_ICONS[option.value ?? 'auto']}
            library="lucide"
          />
        ),
        handler: () => setForceThemeMode(option.value),
        group: 'Theme',
        order: Number.POSITIVE_INFINITY,
      })),
    ],
    [
      showConversationModal,
      showSiteModal,
      hasProjectAccess,
      showProjectModal,
      showClientModal,
      navigate,
      setForceThemeMode,
      themeOptions,
    ],
  );

  useRegisterQuickActions(quickActions);
};
